import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import {
  motion, AnimatePresence, useMotionValue, useSpring, useTransform, useReducedMotion,
} from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, Star, ShieldCheck, Truck } from 'lucide-react';
import { HERO_SLIDES, AUTOPLAY_MS } from './heroSlides';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const EASE = [0.22, 1, 0.36, 1];

/** Text enters as a group: each line lifts and fades a beat after the last. */
const textStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
};

const line = (reduced) => ({
  hidden: { opacity: 0, y: reduced ? 0 : 18 },
  show: { opacity: 1, y: 0, transition: { duration: reduced ? 0.15 : 0.5, ease: EASE } },
});

function FloatingBadge({ icon: Icon, title, subtitle, className, tone = 'default', depth, reduced }) {
  const tones = {
    default: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
  };

  return (
    <motion.div
      style={reduced ? undefined : { x: depth?.x, y: depth?.y }}
      initial={{ opacity: 0, scale: reduced ? 1 : 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.35, ease: EASE }}
      className={`absolute z-20 flex items-center gap-2.5 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(15,23,42,0.35)] ${className}`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${tones[tone]}`} aria-hidden="true" />
      <div className="leading-tight">
        <p className="font-body font-semibold text-secondary text-xs whitespace-nowrap">{title}</p>
        {subtitle && <p className="text-[10px] text-muted-foreground whitespace-nowrap">{subtitle}</p>}
      </div>
    </motion.div>
  );
}

/**
 * Homepage hero carousel.
 *
 * Autoplay is driven by a single `progress` value rather than a timer running
 * alongside a separate progress animation. One source of truth means the bar can
 * never disagree with when the slide actually advances, and pausing is simply
 * stopping the increment.
 *
 * The whole thing is capped in height on purpose. A full-viewport hero pushes
 * every product below the fold, and a store's job above the fold is to show
 * merchandise — the category rail should still peek through underneath.
 */
export function HeroSlider() {
  const reduced = useReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 28, align: 'start' });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef(null);
  const [images, setImages] = useState({});

  /**
   * Slide artwork comes from the categories the admin already maintains, so the
   * hero and the "Browse Collections" card for the same category can never drift
   * apart, and swapping a photo needs no deploy. The bundled URLs stay as a
   * fallback for the first paint and for a failed request.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/categories`);
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.categories || [];
        const bySlug = {};
        for (const c of list) {
          if (c?.slug && c?.image) bySlug[c.slug] = c.image;
        }
        if (!cancelled) setImages(bySlug);
      } catch {
        // Fallback images are already in place; a failed lookup changes nothing.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pointer position, springed, drives the image tilt and badge parallax.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 120, damping: 20, mass: 0.4 });
  const sy = useSpring(py, { stiffness: 120, damping: 20, mass: 0.4 });

  const rotateY = useTransform(sx, [-0.5, 0.5], ['4deg', '-4deg']);
  const rotateX = useTransform(sy, [-0.5, 0.5], ['-3deg', '3deg']);
  const badgeNear = { x: useTransform(sx, [-0.5, 0.5], [14, -14]), y: useTransform(sy, [-0.5, 0.5], [10, -10]) };
  const badgeFar = { x: useTransform(sx, [-0.5, 0.5], [-10, 10]), y: useTransform(sy, [-0.5, 0.5], [-7, 7]) };

  // Hoisted out of the slide loop on purpose: these are hooks, and calling them
  // inside .map() would create a different number of them per render.
  const imgX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);

  const onPointerMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const resetPointer = () => { px.set(0); py.set(0); };

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
    setProgress(0);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect).off('reInit', onSelect); };
  }, [emblaApi, onSelect]);

  // Autoplay. Skipped entirely under reduced motion — a carousel that moves on
  // its own is exactly what that preference is asking us not to do.
  //
  // The ticker only advances `progress`; the slide change is a separate effect.
  // Calling scrollNext() inside the setState updater made it a side effect in a
  // function React is allowed to run more than once — and in StrictMode it does
  // exactly that, firing scrollNext twice per tick and leaving the carousel
  // fighting itself.
  useEffect(() => {
    if (!emblaApi || paused || reduced) return;
    const TICK = 50;
    const id = setInterval(
      () => setProgress((p) => Math.min(1, p + TICK / AUTOPLAY_MS)),
      TICK
    );
    return () => clearInterval(id);
  }, [emblaApi, paused, reduced]);

  useEffect(() => {
    if (progress < 1 || !emblaApi || paused || reduced) return;
    emblaApi.scrollNext();
    setProgress(0);
  }, [progress, emblaApi, paused, reduced]);

  const slide = HERO_SLIDES[selected] ?? HERO_SLIDES[0];
  const L = line(reduced);

  return (
    <section
      ref={sectionRef}
      onMouseMove={reduced ? undefined : onPointerMove}
      onMouseLeave={resetPointer}
      className="relative overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Featured categories"
      data-testid="hero-section"
    >
      {/* Background wash cross-fades between slides. */}
      <AnimatePresence initial={false}>
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`}
          aria-hidden="true"
        />
      </AnimatePresence>

      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        className="relative max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8"
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex touch-pan-y">
            {HERO_SLIDES.map((s, i) => {
              const active = i === selected;
              return (
                <div key={s.id} className="min-w-0 flex-[0_0_100%]" aria-roledescription="slide" aria-label={`${i + 1} of ${HERO_SLIDES.length}`}>
                  <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
                    {/* Copy */}
                    <motion.div
                      variants={textStagger}
                      initial="hidden"
                      animate={active ? 'show' : 'hidden'}
                      className="order-2 lg:order-1"
                    >
                      <motion.span
                        variants={L}
                        className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/70 px-3 py-1.5 text-[11px] font-body font-bold uppercase tracking-widest text-primary backdrop-blur-xl"
                      >
                        <s.icon className="w-3.5 h-3.5" aria-hidden="true" />
                        {s.eyebrow}
                      </motion.span>

                      <motion.h1
                        variants={L}
                        className="mt-3 font-heading text-[28px] sm:text-4xl md:text-[42px] leading-[1.06] tracking-tight text-secondary"
                      >
                        {s.headline}
                      </motion.h1>

                      <motion.p
                        variants={L}
                        className="mt-2.5 max-w-md font-body text-[15px] leading-relaxed text-muted-foreground"
                      >
                        {s.description}
                      </motion.p>

                      <motion.div variants={L} className="mt-5 flex flex-wrap gap-2.5">
                        <Link
                          to={s.primary.to}
                          tabIndex={active ? 0 : -1}
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-body font-semibold text-white shadow-[0_10px_30px_-12px_rgba(4,82,190,0.8)] transition-colors hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        >
                          {s.primary.label}
                          <ArrowRight className="w-4 h-4" aria-hidden="true" />
                        </Link>
                        <Link
                          to={s.secondary.to}
                          tabIndex={active ? 0 : -1}
                          className="inline-flex items-center rounded-full border border-white/70 bg-white/60 px-6 py-2.5 font-body font-semibold text-secondary backdrop-blur-xl transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        >
                          {s.secondary.label}
                        </Link>
                      </motion.div>
                    </motion.div>

                    {/* Product image */}
                    <div className="relative order-1 lg:order-2 isolate" style={{ perspective: 1200 }}>
                      <motion.div
                        style={reduced ? undefined : { rotateX, rotateY, transformStyle: 'preserve-3d' }}
                        initial={{ opacity: 0, scale: reduced ? 1 : 1.06 }}
                        animate={active ? { opacity: 1, scale: 1 } : { opacity: 0.4, scale: 1 }}
                        transition={{ duration: reduced ? 0.2 : 0.8, ease: EASE }}
                        className="relative"
                      >
                        <div className={`pointer-events-none absolute inset-4 rounded-[2.5rem] blur-2xl opacity-70 ${s.glow}`} aria-hidden="true" />

                        <div className="relative aspect-[4/3] lg:aspect-[16/11] max-h-[300px] overflow-hidden rounded-3xl shadow-[0_30px_60px_-25px_rgba(15,23,42,0.45)]">
                          {/* Parallax: the image is oversized and drifts against
                              the frame, so the crop moves less than the pointer. */}
                          <motion.img
                            src={images[s.slug] || s.image}
                            alt={s.alt}
                            loading={i === 0 ? 'eager' : 'lazy'}
                            fetchpriority={i === 0 ? 'high' : undefined}
                            style={reduced ? undefined : { x: imgX, y: imgY }}
                            onError={(e) => {
                              if (e.currentTarget.src !== s.image) e.currentTarget.src = s.image;
                            }}
                            className="h-full w-full scale-110 object-cover"
                          />
                        </div>

                        {active && (
                          <>
                            <FloatingBadge
                              icon={Star} tone="warning" title="4.8 / 5"
                              subtitle="Verified buyers"
                              className="-top-3 left-4 sm:left-6"
                              depth={badgeNear} reduced={reduced}
                            />
                            <FloatingBadge
                              icon={ShieldCheck} tone="success" title="100% Authentic"
                              subtitle="Brand warranty"
                              className="bottom-4 -left-2 sm:-left-4"
                              depth={badgeFar} reduced={reduced}
                            />
                            <FloatingBadge
                              icon={Truck} title="Free delivery"
                              subtitle="On orders above ₹999"
                              className="-bottom-3 right-3 sm:right-6"
                              depth={badgeNear} reduced={reduced}
                            />
                          </>
                        )}
                      </motion.div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center gap-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              aria-label="Previous slide"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/60 bg-white/70 text-secondary backdrop-blur-xl transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              aria-label="Next slide"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/60 bg-white/70 text-secondary backdrop-blur-xl transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Dots double as the progress indicator: the active one fills over the
              autoplay interval, so there is one thing to read, not two. */}
          <div className="flex flex-1 items-center gap-2">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => emblaApi?.scrollTo(i)}
                aria-label={`Go to ${s.eyebrow}`}
                aria-current={i === selected ? 'true' : undefined}
                className="group relative h-1.5 flex-1 max-w-[72px] overflow-hidden rounded-full bg-secondary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-primary transition-transform duration-100 ease-linear"
                  style={{
                    width: '100%',
                    transformOrigin: 'left',
                    transform: `scaleX(${i < selected ? 1 : i === selected ? (reduced ? 1 : progress) : 0})`,
                  }}
                />
              </button>
            ))}
          </div>

          <span className="hidden font-body text-xs tabular-nums text-muted-foreground sm:block" aria-live="polite">
            {String(selected + 1).padStart(2, '0')} / {String(HERO_SLIDES.length).padStart(2, '0')}
          </span>
        </div>
      </div>
    </section>
  );
}
